import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import MainNavPanel from './MainNavPanel';
import ExerciseNavPanel from './ExerciseNavPanel';
import TaskManagementNavPanel from './TaskManagementNavPanel';
import { useClickAway } from 'react-use';
import { useSidebarNavContext } from './SidebarNavProvider';
import dayjs from 'dayjs';
import IncidentWorkspacePanel from './IncidentWorkspacePanel';
import { useLocation, useMatch } from 'react-router';
import NotificationsNavPanel from './NotificationsNavPanel';
import { useTransitionState } from 'react-transition-state';

export type PanelType = 'ics' | 'exercise' | 'task' | 'notifications' | null;

/** TODO: check scrollbars in Windows/Firefox/Safari */
const SidebarNav: React.FC<{
  toggleRef: MutableRefObject<HTMLButtonElement | null>;
}> = ({ toggleRef }) => {
  const { close, isOpen } = useSidebarNavContext();
  const location = useLocation();

  const iapMatch = useMatch({ path: '/iap', end: false });
  const taskMatch = useMatch({ path: '/task_management', end: false });
  const prepareMatch = useMatch({ path: '/prepare', end: false });
  const notificationsMatch = useMatch({ path: '/notifications', end: false });
  const mapMatch = useMatch({ path: '/hub', end: false });

  const [secondPanel, setSecondPanel] = useState<PanelType>(() => {
    if (iapMatch) return 'ics';
    if (taskMatch) return 'task';
    if (prepareMatch) return 'exercise';
    if (notificationsMatch) return 'notifications';
    return null;
  });

  const navRef = useRef(null);

  useClickAway(navRef, (e) => {
    if (isOpen && !toggleRef.current?.contains(e.target as Node)) {
      close();
    }
  });

  useEffect(() => {
    /**
     * NOTE: right now only checking map since there are "Go to Map" buttons.
     * Might make sense to do this and get rid of the "setSecondPanel" pass down
     * prop.
     */
    if (mapMatch) {
      setSecondPanel(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Track which second panel to render (persists during exit animation)
  const [renderedPanel, setRenderedPanel] = useState<PanelType>(secondPanel);

  const showSecondPanel = secondPanel !== null;

  const [{ status }, toggle] = useTransitionState({
    timeout: 300,
    preEnter: true,
    preExit: true,
    initialEntered: showSecondPanel,
  });

  useEffect(() => {
    toggle(showSecondPanel);
  }, [showSecondPanel, toggle]);

  // Update rendered panel when a new second panel is selected
  useEffect(() => {
    if (secondPanel !== null) {
      setRenderedPanel(secondPanel);
    }
  }, [secondPanel]);

  // Apply slider class during enter/entered/preExit phases
  const isSliderActive =
    status === 'entering' || status === 'entered' || status === 'preExit';

  // Keep second panel content mounted until exit animation completes
  const shouldRenderSecondPanel = status !== 'exited' && renderedPanel !== null;

  return (
    <div id="sidebar-nav" className={isOpen ? 'open' : ''} ref={navRef}>
      <div className="sidebar-nav-content">
        <div
          className={`sidebar-nav-slider${isSliderActive ? ' show-second-panel' : ''}`}
        >
          <div className="sidebar-nav-panel" aria-hidden={isSliderActive}>
            <MainNavPanel
              setSecondPanel={(key: PanelType) => setSecondPanel(key)}
            />
          </div>
          <div className="sidebar-nav-panel" aria-hidden={!isSliderActive}>
            {shouldRenderSecondPanel && (
              <SecondPanelSwitch
                secondPanel={renderedPanel}
                navBack={() => setSecondPanel(null)}
              />
            )}
          </div>
        </div>
      </div>
      <div className="sidebar-nav-footer flex-shrink-0 flex-grow-0">
        © {dayjs().format('YYYY')} PRATUS • All rights reserved.
      </div>
    </div>
  );
};

export default SidebarNav;

const SecondPanelSwitch: React.FC<{
  secondPanel: PanelType;
  navBack: () => void;
}> = ({ secondPanel, navBack }) => {
  switch (secondPanel) {
    case 'exercise':
      return <ExerciseNavPanel navBack={navBack} />;
    case 'task':
      return <TaskManagementNavPanel navBack={navBack} />;
    case 'ics':
      return <IncidentWorkspacePanel navBack={navBack} />;
    case 'notifications':
      return <NotificationsNavPanel navBack={navBack} />;
    default:
      return null;
  }
};
